import { trackDigitalEvent } from '@sparknz/tracking';

import { MobilePlanCardsProductsQuery$data } from '__generated__/MobilePlanCardsProductsQuery.graphql';
import { RelatedOfferDetails } from '../typings/plans';
import { getTrackingPromoDetails } from './getPromoDetails';

type PlanCardDetails =
  MobilePlanCardsProductsQuery$data['eligibleProductOffersByCategory']['category']['plans'];

interface PlanTrackingInformation
  extends Pick<
    PlanCardDetails[0],
    | 'price'
    | 'dataLayerCategory'
    | 'productCharacteristics'
    | 'contractTerm'
    | 'promotions'
  > {
  planDetails: Pick<
    PlanCardDetails[0]['planDetails'],
    'planId' | 'planName' | 'categoryIdentifiers'
  >;
}

export type EventTrackingConfiguration = {
  planDetails: PlanCardDetails | PlanTrackingInformation[];
  isBusiness: boolean;
  eventName?: string;
};

export function trackActionLayerViewEvent({
  planDetails,
  isBusiness,
  eventName = 'Shop Mobile Plans Selection Action Layer',
}: EventTrackingConfiguration) {
  const planSelectionEvent = {
    event: 'actionLayer.view',
    w3cddl: {
      event: [
        {
          eventInfo: {
            eventName,
            eventAction: 'actionLayer.view',
          },
        },
      ],
      product: planDetails.map((plan) => {
        const priceValue = isBusiness
          ? String(plan?.price?.basePriceExcludingTax)
          : String(plan?.price.basePrice);
        return {
          productInfo: {
            productID: plan?.planDetails?.planId,
            productName: plan?.planDetails?.planName,
            manufacturer: 'Spark',
            sku: plan?.planDetails?.planId,
          },
          category: {
            primaryCategory: plan?.dataLayerCategory || undefined,
          },
          attributes: {
            displayName: plan?.planDetails?.planName,
            itemBasePrice: priceValue,
            itemOneOffCost:
              plan?.price?.priceType === 'Recurring' ? '00.00' : priceValue,
            itemRecurringCost:
              plan?.price?.priceType === 'Recurring' ? priceValue : '00.00',
            promoMessage: getTrackingPromoDetails(plan?.promotions).dealText,
            promoTag: getTrackingPromoDetails(plan?.promotions).marketingTag,
            isGSTIncluded: !isBusiness,
            baseStore: isBusiness ? 'businessshop' : 'personalshop',
            paymentType:
              plan?.productCharacteristics?.balanceManagement || undefined,
            contractTerm: plan?.contractTerm?.name || undefined,
            productSpecification: 'Mobile Subscription',
            segment: isBusiness ? 'BUSINESS' : 'CONSUMER',
            categoryIdentifier:
              plan?.planDetails?.categoryIdentifiers?.join(','),
          },
        };
      }),
    },
  };

  trackDigitalEvent(planSelectionEvent);
}

/**
 * Utility function to transform a base offer detail into a format
 * that is compatible with the existing mobile plans tracking implementation
 */
export const toPlanCardProduct = (
  plan: RelatedOfferDetails,
): PlanTrackingInformation => ({
  ...plan,
  planDetails: {
    planId: plan.id,
    planName: plan.name ?? '',
    categoryIdentifiers: plan.category?.map(({ id }) => id) ?? [],
  },
  price: {
    priceType: plan.price?.priceType ?? '',
    basePrice: plan.price?.basePrice ?? null,
    basePriceExcludingTax: plan.price?.basePriceExcludingTax ?? null,
    priceFrequency: plan.price?.frequency?.value ?? null,
    pricePeriod: plan.price?.frequency?.period ?? null,
  },
});
